import React, { useState } from 'react';
import ReplyForm from './ReplyForm';
import ReplyItem from './ReplyItem';
import { MdKeyboardArrowDown, MdKeyboardArrowUp, MdReply, MdThumbUp } from 'react-icons/md';

const CommentItem = ({
  comment,
  currentUser,
  onCommentLike,
  onAddReply,
  postId
}) => {
  const [isReplying, setIsReplying] = useState(false);
  const [showAllReplies, setShowAllReplies] = useState(false);
  
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "Just now";
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };
  
  const handleSubmitReply = (replyText) => {
    const newReply = {
      comment_id: Math.floor(Math.random() * 10000),
      comment: replyText,
      comment_user_name: currentUser.name,
      comment_user_profile: currentUser.profile_image,
      created_at: new Date().toISOString(),
      comment_likes: 0,
      has_liked_comment: false
    };
    
    onAddReply(comment.comment_id, newReply);
    setIsReplying(false);
  };

  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0">
        {comment.comment_user_profile ? (
          <img
            src={comment.comment_user_profile}
            alt={comment.comment_user_name}
            className="w-8 h-8 rounded-full object-cover border border-gray-200"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white font-semibold text-sm border border-gray-200">
            {comment.comment_user_name?.[0]?.toUpperCase() || 'U'}
          </div>
        )}
      </div>

      <div className="flex-1">
        <div className="bg-gray-50 rounded-2xl p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <p className="font-medium text-sm font-semibold text-gray-900 hover:underline hover:text-blue-600 cursor-pointer">
                {comment.comment_user_name || "User"}
              </p>
              {comment.is_author && (
                <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
                  Author
                </span>
              )}
            </div>
            <span className="text-xs text-gray-500">
              {formatTimeAgo(comment.created_at)}
            </span>
          </div>

          <p className="text-sm text-gray-800 mt-1 whitespace-pre-line">
            {comment.comment}
          </p>

          <div className="flex items-center mt-2 text-xs text-gray-500">
            <button
              className={`flex items-center gap-1 hover:text-blue-600 ${
                comment.has_liked_comment ? "text-blue-600" : ""
              }`}
              onClick={() => onCommentLike(comment.comment_id, comment.has_liked_comment)}
            >
              <MdThumbUp className={`text-base ${comment.has_liked_comment ? "text-blue-600" : ""}`} />
              {comment.comment_likes > 0 && (
                <span>{comment.comment_likes}</span>
              )}
              <span>Like</span>
            </button>

            <span className="mx-2 text-gray-400">|</span>

            <button
              className="flex items-center gap-1 hover:text-blue-600"
              onClick={() => setIsReplying(!isReplying)}
            >
              <MdReply className="text-base" />
              <span className="ml-1">Reply</span>
              {comment.replies && comment.replies.length > 0 && (
                <span className="ml-1">({comment.replies.length})</span>
              )}
            </button>
          </div>
        </div>

        {/* Replies Section */}
        {comment.replies && comment.replies.length > 0 && (
          <div className="mt-2 ml-2 pl-4 border-l-2 border-gray-200">
            {comment.replies
              .slice(0, showAllReplies ? comment.replies.length : 2)
              .map((reply) => (
                <ReplyItem
                  key={reply.comment_id}
                  reply={reply}
                  onCommentLike={onCommentLike}
                  formatTimeAgo={formatTimeAgo}
                />
              ))}

            {comment.replies.length > 2 && (
              <button
                onClick={() => setShowAllReplies(!showAllReplies)}
                className="w-full text-left text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors mt-2"
              >
                <div className="inline-flex items-center px-4 py-1.5 rounded-full hover:bg-gray-100">
                  {showAllReplies ? (
                    <>
                      <MdKeyboardArrowUp className="mr-1 text-lg" />
                      Collapse replies
                    </>
                  ) : (
                    <>
                      <MdKeyboardArrowDown className="mr-1 text-lg" />
                      See {comment.replies.length - 2} more{" "}
                      {comment.replies.length - 2 === 1
                        ? "reply"
                        : "replies"}
                    </>
                  )}
                </div>
              </button>
            )}
          </div>
        )}

        {/* Reply Form */}
        {isReplying && (
          <div className="mt-3 ml-2 pl-4 border-l-2 border-gray-200">
            <ReplyForm
              currentUser={currentUser}
              onSubmitReply={handleSubmitReply}
              onCancel={() => setIsReplying(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CommentItem;